import type { Prospect, ProspectAnalysis } from '../types';

const BASE = '/cms-api';
const LS_PROSPECTS = 'cms_prospects';

function api<T>(endpoint: string, method = 'GET', body?: unknown): Promise<T> {
  return fetch(`${BASE}/${endpoint}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => r.json());
}

// ─── localStorage fallback (CDN static — PHP indisponibil) ───────────────────
const localLoad = (): Prospect[] => {
  try { return JSON.parse(localStorage.getItem(LS_PROSPECTS) || '[]'); } catch { return []; }
};
const localSave = (p: Prospect[]) => localStorage.setItem(LS_PROSPECTS, JSON.stringify(p));

export const prospectsApi = {
  list: async (): Promise<Prospect[]> => {
    try { return await api<Prospect[]>('prospects.php'); }
    catch { return localLoad(); }
  },

  get: async (id: string): Promise<Prospect> => {
    try { return await api<Prospect>(`prospects.php?id=${id}`); }
    catch {
      const p = localLoad().find(x => x.id === id);
      if (!p) throw new Error('Not found');
      return p;
    }
  },

  create: async (data: Omit<Prospect, 'id' | 'createdAt' | 'updatedAt' | 'domain' | 'analysisStatus'>): Promise<Prospect> => {
    try { return await api<Prospect>('prospects.php', 'POST', data); }
    catch {
      const prospect: Prospect = {
        ...data, id: 'pr-' + Date.now(),
        domain: extractDomain(data.url),
        analysisStatus: 'pending',
        createdAt: new Date().toISOString().split('T')[0],
        updatedAt: new Date().toISOString().split('T')[0],
      };
      const all = localLoad();
      all.unshift(prospect);
      localSave(all);
      return prospect;
    }
  },

  update: async (id: string, data: Partial<Prospect>): Promise<Prospect> => {
    try { return await api<Prospect>('prospects.php', 'PUT', { id, ...data }); }
    catch {
      const all = localLoad().map(p => p.id === id ? { ...p, ...data, updatedAt: new Date().toISOString().split('T')[0] } : p);
      localSave(all);
      return all.find(p => p.id === id)!;
    }
  },

  delete: async (id: string): Promise<void> => {
    try { await api('prospects.php', 'DELETE', { id }); }
    catch { localSave(localLoad().filter(p => p.id !== id)); }
  },

  analyze: async (id: string, url: string): Promise<{ ok: boolean; analysis?: ProspectAnalysis; error?: string }> => {
    try {
      const res = await api<{ ok: boolean; analysis: ProspectAnalysis }>('analyze.php', 'POST', { id, url });
      if (res.ok && res.analysis) {
        const all = localLoad().map(p =>
          p.id === id ? { ...p, analysis: res.analysis, analysisStatus: 'done' as const, updatedAt: new Date().toISOString().split('T')[0] } : p
        );
        localSave(all);
      }
      return res;
    } catch {
      // Pe CDN static PHP nu rulează — marchăm ca eroare, nu fabricăm date false
      const all = localLoad().map(p =>
        p.id === id ? { ...p, analysisStatus: 'error' as const, updatedAt: new Date().toISOString().split('T')[0] } : p
      );
      localSave(all);
      return { ok: false, error: 'Analiza necesită PHP pe server live. Pe serverul de producție va funcționa complet.' };
    }
  },
};

export function extractDomain(url: string): string {
  try { return new URL(url.startsWith('http') ? url : 'https://' + url).hostname.replace('www.', ''); }
  catch { return url; }
}
